import React, { useState } from "react";
import { Link } from "@tanstack/react-router";

import { TOO_MANY_ATTEMPTS, isThrottled, passwordReset } from "@/api/auth";

// Civic-editorial, matching the claim page (--cl-* tokens live in index.css
// under .claim-scope). The whole flow reads as one product.
const card =
  "w-full max-w-[460px] rounded-[14px] border border-[var(--cl-line)] bg-[var(--cl-surface)] p-8 sm:p-10 shadow-sm";
const btn =
  "inline-flex w-full items-center justify-center rounded-[8px] bg-[var(--cl-teal)] px-5 py-3 text-[14px] font-semibold text-[var(--cl-btn-ink)] transition-opacity hover:opacity-90 disabled:opacity-45";
const input =
  "w-full rounded-[8px] border border-[var(--cl-line-2)] bg-[var(--cl-raise)] px-3.5 py-2.5 text-[14px] text-[var(--cl-ink)] outline-none placeholder:text-[var(--cl-faint)] focus:border-[var(--cl-teal)]";

const PasswordResetRequest: React.FC = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !emailValid) return;
    setSubmitting(true);
    setError("");
    try {
      await passwordReset(email.trim());
    } catch (err: unknown) {
      // Never reveal whether the account exists: only a 429 or a real server
      // failure shows an error; anything else lands on the neutral inbox screen.
      if (isThrottled(err)) {
        setSubmitting(false);
        setError(TOO_MANY_ATTEMPTS);
        return;
      }
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status && status >= 500) {
        setSubmitting(false);
        setError("Something went wrong on our side. Give it a moment and try again.");
        return;
      }
    }
    setSubmitting(false);
    setSent(true);
  };

  return (
    <div
      className="claim-scope flex min-h-screen w-full items-center justify-center bg-[var(--cl-paper)] px-4 py-10"
      style={{ fontFamily: "var(--cl-sans)" }}
    >
      <div className={card}>
        <span
          className="text-[11px] uppercase tracking-[0.22em] text-[var(--cl-teal-ink)]"
          style={{ fontFamily: "var(--cl-mono)" }}
        >
          CodeClear
        </span>

        {sent ? (
          <>
            <h1
              className="mt-4 text-[26px] leading-[1.12] text-[var(--cl-ink)]"
              style={{ fontFamily: "var(--cl-serif)", textWrap: "balance" }}
            >
              Check your inbox
            </h1>
            <p className="mt-3 text-[14px] leading-relaxed text-[var(--cl-ink-2)]">
              If an account exists for{" "}
              <span className="text-[var(--cl-ink)]">{email.trim()}</span>, a
              reset link is on its way. It works once and expires in 72 hours.
            </p>
            <div className="mt-7">
              <Link to="/login" className={btn}>
                Back to sign in
              </Link>
            </div>
            <p className="mt-6 text-[12px] leading-relaxed text-[var(--cl-mute)]">
              Nothing after a few minutes? Check spam, or reach your CodeClear
              contact.
            </p>
          </>
        ) : (
          <form onSubmit={submit} noValidate>
            <h1
              className="mt-4 text-[26px] leading-[1.12] text-[var(--cl-ink)]"
              style={{ fontFamily: "var(--cl-serif)", textWrap: "balance" }}
            >
              Reset your password
            </h1>
            <p className="mt-3 text-[14px] leading-relaxed text-[var(--cl-ink-2)]">
              Enter your email and we will send a link to set a new one.
            </p>
            <div className="mt-6">
              <label
                htmlFor="reset-email"
                className="mb-1.5 block text-[8.5px] uppercase tracking-[0.18em] text-[var(--cl-mute)]"
                style={{ fontFamily: "var(--cl-mono)" }}
              >
                Email
              </label>
              <input
                id="reset-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                className={`${input} ${
                  touched && !emailValid ? "border-[var(--cl-danger)]" : ""
                }`}
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched(true)}
              />
              {touched && !emailValid && (
                <p className="mt-1.5 text-[12px] text-[var(--cl-danger)]">
                  Enter a valid email address.
                </p>
              )}
            </div>
            {error && (
              <p className="mt-2 text-[12px] text-[var(--cl-danger)]">{error}</p>
            )}
            <div className="mt-6">
              <button
                type="submit"
                className={btn}
                disabled={submitting || !emailValid}
              >
                {submitting ? "Sending…" : "Send reset link"}
              </button>
            </div>
            <p className="mt-6 text-[12px] text-[var(--cl-mute)]">
              Remembered it?{" "}
              <Link to="/login" className="text-[var(--cl-teal-ink)] underline">
                Back to sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default PasswordResetRequest;
